import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Check, Trash2, User, AlertTriangle, CreditCard, ChevronRight, MessageCircle } from 'lucide-react';
import { AVATARS, DEFAULT_AVATAR_ID, getAvatar } from '../utils/avatars';

function AjustesPage() {
  const navigate = useNavigate();
  const { user, updateAvatar, deleteAccount } = useAuth();
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const currentAvatarId = user?.avatar || DEFAULT_AVATAR_ID;
  const currentAvatar = getAvatar(currentAvatarId);

  const handlePickAvatar = async (avatarId) => {
    if (avatarId === currentAvatarId || saving) return;
    setSaving(true);
    try {
      await updateAvatar(avatarId);
    } catch (error) {
      console.error('Failed to update avatar:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await deleteAccount();
      // Full reload to flush any cached Supabase client state after account deletion
      window.location.href = '/';
    } catch (error) {
      console.error('Failed to delete account:', error);
      setDeleting(false);
    }
  };

  return (
    <Layout showMonthSelector={false}>
      <div className="space-y-6 animate-fade-in" data-testid="ajustes-page">
        {/* Profile Card */}
        <Card className="border-2 border-slate-200 shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-[#1B2A4A] flex items-center gap-3">
              <User size={24} />
              Sua conta
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className={`w-20 h-20 rounded-2xl flex items-center justify-center ${currentAvatar.bg}`}>
                <currentAvatar.Icon size={44} className={currentAvatar.color} strokeWidth={1.75} />
              </div>
              <div>
                <p className="text-lg font-semibold text-[#1B2A4A]">{user?.nome || 'Usuário'}</p>
                <p className="text-base text-slate-500">{user?.email}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Minha Conta shortcut */}
        <Card className="border-2 border-slate-200 shadow-lg">
          <CardContent className="p-0">
            <button
              onClick={() => navigate('/minha-conta')}
              className="w-full flex items-center gap-4 p-5 text-left hover:bg-slate-50 active:bg-slate-100 transition-all rounded-lg"
              data-testid="minha-conta-link"
            >
              <div className="w-12 h-12 rounded-xl bg-emerald-50 text-[#047857] flex items-center justify-center shrink-0">
                <CreditCard size={24} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-lg font-bold text-[#1B2A4A]">Minha Conta</p>
                <p className="text-base text-slate-500">
                  Ver plano, cancelar ou fazer upgrade
                </p>
              </div>
              <ChevronRight size={24} className="text-slate-400 shrink-0" />
            </button>
          </CardContent>
        </Card>

        {/* Fale conosco */}
        <Card className="border-2 border-slate-200 shadow-lg">
          <CardContent className="p-0">
            <button
              onClick={() => navigate('/contato')}
              className="w-full flex items-center gap-4 p-5 text-left hover:bg-slate-50 active:bg-slate-100 transition-all rounded-lg"
              data-testid="contato-link"
            >
              <div className="w-12 h-12 rounded-xl bg-sky-50 text-sky-700 flex items-center justify-center shrink-0">
                <MessageCircle size={24} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-lg font-bold text-[#1B2A4A]">Fale conosco</p>
                <p className="text-base text-slate-500">
                  Dúvidas, sugestões ou problemas? Envie uma mensagem
                </p>
              </div>
              <ChevronRight size={24} className="text-slate-400 shrink-0" />
            </button>
          </CardContent>
        </Card>

        {/* Avatar Picker */}
        <Card className="border-2 border-slate-200 shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-[#1B2A4A]">
              Escolha seu avatar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-5 gap-3">
              {AVATARS.map((av) => {
                const selected = av.id === currentAvatarId;
                const Icon = av.Icon;
                return (
                  <button
                    key={av.id}
                    onClick={() => handlePickAvatar(av.id)}
                    disabled={saving}
                    className={`relative aspect-square rounded-2xl border-2 flex items-center justify-center transition-all ${av.bg} ${
                      selected
                        ? 'border-[#047857] scale-105'
                        : 'border-slate-200 hover:border-[#1B2A4A] hover:scale-105'
                    }`}
                    data-testid={`avatar-${av.id}`}
                  >
                    <Icon size={32} className={av.color} strokeWidth={1.75} />
                    {selected && (
                      <span className="absolute -top-2 -right-2 w-6 h-6 bg-[#047857] text-white rounded-full flex items-center justify-center">
                        <Check size={14} strokeWidth={3} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <p className="text-sm text-slate-500 mt-4">
              Toque em um avatar para escolher.
            </p>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="border-2 border-red-300 shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-red-700 flex items-center gap-3">
              <AlertTriangle size={24} />
              Zona de perigo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-base text-slate-600">
              Excluir sua conta apaga permanentemente todos os seus dados: receitas, despesas, metas e histórico.
              Esta ação não pode ser desfeita.
            </p>
            <Button
              onClick={() => setShowDeleteConfirm(true)}
              variant="outline"
              className="h-12 px-6 text-base font-semibold border-2 border-red-500 text-red-600 hover:bg-red-500 hover:text-white rounded-xl"
              data-testid="delete-account-btn"
            >
              <Trash2 size={20} className="mr-2" />
              Excluir minha conta
            </Button>
          </CardContent>
        </Card>

        {/* Delete Confirmation */}
        <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <AlertDialogContent data-testid="delete-account-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-xl font-bold text-red-700">
                Excluir conta permanentemente?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-base">
                Todos os seus dados serão apagados e não poderão ser recuperados.
                Você será desconectado e voltará para a página inicial.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-3">
              <AlertDialogCancel disabled={deleting} className="h-12 px-6 text-base border-2">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="h-12 px-6 text-base bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-account-btn"
              >
                <Trash2 size={18} className="mr-2" />
                {deleting ? 'Excluindo...' : 'Sim, excluir tudo'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}

export default AjustesPage;
